import { IContactNotifications } from "../Interfaces/ICompanyContact";

/**
 * The contact's TDDA access flags as displayed on the record's contact grid (CR.5): derived from the
 * flags, "Not set" when neither has been chosen — a state the record allows and wizard step 4 does
 * not.
 */
export const imperansRoleLabel = (
  isRequestingTestPersonnel?: boolean | null,
  hasResultsAccess?: boolean | null,
): string => {
  if (isRequestingTestPersonnel && hasResultsAccess) return "RTP + Results Access";
  if (isRequestingTestPersonnel) return "RTP";
  if (hasResultsAccess) return "Results Access";
  return "Not set";
};

/**
 * The five Imperans customer roles (S4.1), keyed on the Imperans RoleID itself — the same values the
 * server's ImperansContactRoleEnum holds (bug 6363). The CRM used to number them 1, 2, 3 and export
 * those as Imperans ids, which made a point of contact the global Administrator. Unset reads "Not
 * set" on the record.
 */
export const IMPERANS_CONTACT_ROLES = [
  { value: 3, label: "Customer Admin" },
  { value: 9, label: "Customer Finance" },
  { value: 10, label: "Customer Drug Test Results" },
  { value: 11, label: "Customer Booking" },
  { value: 14, label: "Customer Flu Voucher" },
];

export const imperansAccessRoleLabel = (roleID?: number | null): string =>
  IMPERANS_CONTACT_ROLES.find((role) => role.value === roleID)?.label ?? "Not set";

/** No sites ticked means every site, including sites added later (S4.1). */
export const siteAccessLabel = (
  siteIDs: number[] | null | undefined,
  sites: { companySiteID: number; officeName?: string | null }[],
): string => {
  if (!siteIDs || !siteIDs.length) return "All sites";
  const named = siteIDs
    .map((id) => sites.find((site) => site.companySiteID === id)?.officeName)
    .filter(Boolean);
  return named.length ? named.join(", ") : `${siteIDs.length} sites`;
};

type ContactFlags = {
  isPrimaryContact?: boolean | null;
  isRequestingTestPersonnel?: boolean | null;
  hasResultsAccess?: boolean | null;
  isPointOfContact?: boolean;
  isSafetyCritical?: boolean;
};

/** Every flag as a tag — what the review summary and the SOP print (S4.6). */
export const contactFlagTags = (contact: ContactFlags): string[] => {
  const tags: string[] = [];
  if (contact.isPrimaryContact) tags.push("Main");
  if (contact.isRequestingTestPersonnel) tags.push("RTP");
  if (contact.hasResultsAccess) tags.push("Results access");
  if (contact.isPointOfContact) tags.push("Point of contact");
  if (contact.isSafetyCritical) tags.push("Safety critical");
  return tags;
};

/**
 * The flags the grid tags. Main and RTP are left out because the grid already carries them as their
 * own radio columns — showing them twice, once as a control and again as a chip, is what made the
 * Flags column read inconsistently. Every flag is still readable from the grid without opening the
 * contact (S4.6): two as radios, the rest as tags.
 */
export const contactGridFlagTags = (contact: ContactFlags): string[] => {
  const tags: string[] = [];
  if (contact.hasResultsAccess) tags.push("Results access");
  if (contact.isPointOfContact) tags.push("Point of contact");
  if (contact.isSafetyCritical) tags.push("Safety critical");
  return tags;
};

export const RESULT_TYPE_LABELS: { key: keyof IContactNotifications; label: string }[] = [
  { key: "notifyNegativeScreen", label: "Negative Screen" },
  { key: "notifyNonNegativeScreen", label: "Non-Negative Screen" },
  { key: "notifyNegativeLab", label: "Negative Lab" },
  { key: "notifyPositiveLab", label: "Positive Lab" },
];

/**
 * The plain-English restatement of a contact's notification settings (S4.3): it names the chosen
 * result types, and the occasions and sites when the scope is narrowed — including the case where
 * nothing is ticked, which is valid and means nothing is emailed.
 */
export const notificationSummary = (
  notifications: IContactNotifications | null | undefined,
  occasionOptions: { key: string; label: string }[],
  sites: { companySiteID: number; officeName?: string | null }[],
): string => {
  if (!notifications) return "No result types selected — nothing will be emailed.";

  const types = RESULT_TYPE_LABELS.filter((type) => notifications[type.key]).map((t) => t.label);
  if (!types.length) return "No result types selected — nothing will be emailed.";

  const typeText = types.join(", ");
  if (!notifications.limitScope)
    return `Emailed ${typeText} for every occasion and site they can access.`;

  const occasions = (notifications.occasions || [])
    .map((key) => occasionOptions.find((option) => option.key === key)?.label || key)
    .filter(Boolean);
  const siteNames = (notifications.siteIDs || [])
    .map((id) => sites.find((site) => site.companySiteID === id)?.officeName)
    .filter(Boolean);

  const occasionText = occasions.length ? occasions.join(", ") : "every occasion";
  const siteText = siteNames.length ? siteNames.join(", ") : "every site they can access";
  return `Emailed ${typeText} for ${occasionText} at ${siteText}.`;
};

/** Defaults for a contact stored before the notification block existed (S4.3). */
export const defaultNotifications = (): IContactNotifications => ({
  notifyNegativeScreen: true,
  notifyNonNegativeScreen: true,
  notifyNegativeLab: true,
  notifyPositiveLab: true,
  limitScope: false,
  occasions: [],
  siteIDs: [],
});
